import i18n from "../../i18n";
import React, { useEffect, useRef, useState } from "react";
import { Button, Group, Pagination, ScrollArea, Select, Table, Tabs, Text } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import moment from "moment";
import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { useCellRender } from "../../hooks/useCellRender";

const PAGE_SIZES = [10, 20, 30, 40, 50];

const formatWhen = (value) =>
  value ? moment(value).format("YYYY-MM-DD HH:mm") : "—";

const normalizePageSize = (value) => {
  const size = Number(value);
  return PAGE_SIZES.includes(size) ? size : 50;
};

const AckTable = ({
  rows,
  showAction,
  acknowledgingId,
  onAcknowledge,
  cellRender,
  emptyLabel,
}) => {
  if (!rows.length) {
    return (
      <Text color="dimmed" py="sm">
        {emptyLabel}
      </Text>
    );
  }

  return (
    <ScrollArea>
      <Table highlightOnHover verticalSpacing="sm" sx={{ minWidth: 980 }}>
        <thead>
          <tr>
            <th>{i18n.t("Order")}</th>
            <th>{i18n.t("Product")}</th>
            <th>Policy No.</th>
            <th>{i18n.t("Client")}</th>
            <th>{i18n.t("Consultants")}</th>
            <th>{i18n.t("Requested")}</th>
            <th>{i18n.t("Acknowledged")}</th>
            {showAction ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={String(row.orderId)}>
              <td>{cellRender.Order.code(row)}</td>
              <td>
                {row.product ? (
                  cellRender.ProductInfo(row.product)
                ) : row.productType || row.provider ? (
                  <Text color="dimmed">{cellRender.ProductInfo2(row)}</Text>
                ) : (
                  "—"
                )}
              </td>
              <td>
                <Text size="xs">{row.policyNumber || "—"}</Text>
                {row.policyCommenceDate ? (
                  <Text color="dimmed" size="xs" sx={{ whiteSpace: "nowrap" }}>
                    {moment(row.policyCommenceDate).format("YYYY-MM-DD")}
                  </Text>
                ) : null}
              </td>
              <td>{row.client ? cellRender.ClientInfo(row.client, false) : "—"}</td>
              <td>{cellRender.Users(row.consultants, 3, true)}</td>
              <td style={{ whiteSpace: "nowrap" }}>{formatWhen(row.notifiedAt)}</td>
              <td style={{ whiteSpace: "nowrap" }}>{formatWhen(row.acknowledgedAt)}</td>
              {showAction ? (
                <td>
                  <Group position="right">
                    <Button
                      size="xs"
                      loading={String(acknowledgingId) === String(row.orderId)}
                      onClick={() => onAcknowledge(row)}
                    >
                      Acknowledge
                    </Button>
                  </Group>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </Table>
    </ScrollArea>
  );
};

const ListFooter = ({ page, total, pageSize, onPageChange, onPageSizeChange }) => {
  const totalPage = Math.max(1, Math.ceil((Number(total) || 0) / pageSize));
  return (
    <Group position="right" mt="sm">
      <Pagination
        size="xs"
        page={Math.min(page, totalPage)}
        total={totalPage}
        siblings={1}
        onChange={onPageChange}
      />
      <Select
        data={PAGE_SIZES.map((size) => ({ value: String(size), label: String(size) }))}
        value={String(pageSize)}
        onChange={onPageSizeChange}
        size="xs"
        sx={{ width: 80 }}
      />
      <Text size="xs" color="dimmed">
        {Number(total) || 0} Records
      </Text>
    </Group>
  );
};

const UplineAcknowledge = () => {
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [cellRender] = useCellRender();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [acknowledged, setAcknowledged] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [acknowledgedTotal, setAcknowledgedTotal] = useState(0);
  const [pendingPage, setPendingPage] = useState(1);
  const [acknowledgedPage, setAcknowledgedPage] = useState(1);
  const [storedPageSize, setStoredPageSize] = useLocalStorage({
    key: "mbid-page-size",
    defaultValue: 50,
  });
  const pageSize = normalizePageSize(storedPageSize);
  const [acknowledgingId, setAcknowledgingId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const page = tab === "acknowledged" ? acknowledgedPage : pendingPage;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const data = await apiRef.current.Order.listMyUplineAcknowledgements({
          status: tab,
          page,
          limit: pageSize,
        });
        if (cancelled) return;
        const nextPendingTotal = data?.pendingTotal ?? 0;
        const nextAcknowledgedTotal = data?.acknowledgedTotal ?? 0;
        const total = tab === "acknowledged" ? nextAcknowledgedTotal : nextPendingTotal;
        const totalPage = Math.max(1, Math.ceil(total / pageSize));
        if (page > totalPage) {
          if (tab === "acknowledged") setAcknowledgedPage(totalPage);
          else setPendingPage(totalPage);
          return;
        }
        const rows = data?.rows ?? [];
        if (tab === "acknowledged") setAcknowledged(rows);
        else setPending(rows);
        setPendingTotal(nextPendingTotal);
        setAcknowledgedTotal(nextAcknowledgedTotal);
      } catch (error) {
        if (cancelled) return;
        if (tab === "acknowledged") setAcknowledged([]);
        else setPending([]);
        showNotification({
          color: "red",
          title: "Upline Acknowledge",
          message: error?.message || "Failed to load acknowledgements",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tab, page, pageSize, reloadKey]);

  const onAcknowledge = async (row) => {
    setAcknowledgingId(row.orderId);
    try {
      await apiRef.current.Order.acknowledgeUpline(row.orderId);
      showNotification({
        title: "Acknowledged",
        message: "Your acknowledgement has been recorded.",
      });
      setReloadKey((current) => current + 1);
    } catch (error) {
      showNotification({
        color: "red",
        title: "Upline Acknowledge",
        message: error?.message || "Failed to acknowledge the order",
      });
    } finally {
      setAcknowledgingId(null);
    }
  };

  const onPageSizeChange = (value) => {
    const next = normalizePageSize(value);
    setStoredPageSize(next);
    setPendingPage(1);
    setAcknowledgedPage(1);
  };

  const total = tab === "acknowledged" ? acknowledgedTotal : pendingTotal;

  return (
    <Page title="Upline Acknowledge">
      <Tabs value={tab} onTabChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="pending">
            Pending acknowledgement ({pendingTotal})
          </Tabs.Tab>
          <Tabs.Tab value="acknowledged">
            Acknowledged ({acknowledgedTotal})
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="pending" pt="md">
          {loading && tab === "pending" ? (
            <Text color="dimmed">Loading...</Text>
          ) : (
            <AckTable
              rows={pending}
              showAction
              acknowledgingId={acknowledgingId}
              onAcknowledge={onAcknowledge}
              cellRender={cellRender}
              emptyLabel="No pending upline acknowledgements."
            />
          )}
        </Tabs.Panel>
        <Tabs.Panel value="acknowledged" pt="md">
          {loading && tab === "acknowledged" ? (
            <Text color="dimmed">Loading...</Text>
          ) : (
            <AckTable
              rows={acknowledged}
              showAction={false}
              acknowledgingId={null}
              onAcknowledge={onAcknowledge}
              cellRender={cellRender}
              emptyLabel="No acknowledged records."
            />
          )}
        </Tabs.Panel>
      </Tabs>
      <ListFooter
        page={page}
        total={total}
        pageSize={pageSize}
        onPageChange={tab === "acknowledged" ? setAcknowledgedPage : setPendingPage}
        onPageSizeChange={onPageSizeChange}
      />
    </Page>
  );
};

export default UplineAcknowledge;
